import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { eq, or } from 'tio-ui/utilities';
import { Section } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import { service } from '@ember/service';
import {
  getSingleValueForTasField,
  getConfigForTasField,
  getSortedOptionsByFieldName,
  type FieldName,
  type TemplateFieldName,
} from 'tio-common/utils/tuition-assistance/fields';
import Button from 'tio-ui/components/button';
import ReFormField from 'tio-ui/components/form-field';
import Input from 'tio-ui/components/input';
import type { InputType } from 'tio-ui/components/input';
import ReSelect from 'tio-ui/components/select';
import Component from '@glimmer/component';
import inputmask from 'tio-common/modifiers/inputmask';
import SchoolSelect from 'tio-common/components/tuition-assistance/program-details/school-select';
import AlertBanner from 'tio-ui/components/alert-banner';
import TuitionAssistanceFormsCustomFieldsForProvince from 'tio-common/components/tuition-assistance/forms/custom-fields-for-province';
import { divide } from 'tio-ui/math';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { PhCalendarBlank } from 'ember-phosphor-icons';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type { TASProgramInstanceModelFieldsSignature } from 'tio-data/data/resources/tas-program-instance.type';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/data/resources/tas-program-template.type';
import type { TasUnifiedInstitution as TasUnifiedInstitutionModel } from 'tio-data/data/resources/tas-unified-institution.type';
import type { CustomFieldSignature } from 'tio-common/types/tuition-assistance';
import type { TasUnifiedInstitution } from 'tio-data/data/resources/search.ext';
import type IntlService from 'ember-intl/services/intl';
import type { FormField } from './wizard';

interface StepProgramDetailsSignature {
  Args: {
    stepNumber: number;
    programTemplate: TasProgramTemplateModel;
    instanceFields: TASProgramInstanceModelFieldsSignature;
    selectedSchool?: TasUnifiedInstitutionModel;
    isActiveParticipant: boolean;
    isLpnSchoolSelected: boolean;
    programFormFields: FormField[];
    customEmployeeFields: CustomFieldSignature[];
    customProgramFields: CustomFieldSignature[];
    customFieldValidationErrors: { errors: string[]; value: string; path: string }[];
    hasAttempted: boolean;
    onSelectSchool: (school?: TasUnifiedInstitution) => void;
    onUpdateValueForField: (
      fieldName: FieldName,
      programField: FormField,
      value: string | string[] | number,
      event?: Event
    ) => void;
    onUpdateDateFieldValue: (fieldName: FieldName, dates: Date[]) => void;
    getSingleValueForField: (fieldName: keyof TASProgramTemplateModelFieldsSignature) => string;
    onDidUpdateEmployeeCustomFields: (fields: CustomFieldSignature[]) => void;
    onDidUpdateProgramCustomFields: (fields: CustomFieldSignature[]) => void;
    onNext: () => void;
  };
}

export default class StepProgramDetails extends Component<StepProgramDetailsSignature> {
  @service declare intl: IntlService;

  get displayNumber() {
    return this.args.stepNumber + 1;
  }

  private VALID_INPUT_TYPES = new Set<InputType>([
    'text',
    'email',
    'number',
    'password',
    'search',
    'tel',
    'date',
    'url',
  ]);

  asInputType = (type: string | null | undefined): InputType => {
    if (type && this.VALID_INPUT_TYPES.has(type as InputType)) {
      return type as InputType;
    }
    return 'text';
  };

  fieldConfig = (fieldName: string) => {
    return getConfigForTasField(fieldName as TemplateFieldName, this.args.programTemplate.fields);
  };

  fieldOptions = (fieldName: string) => {
    const config = this.fieldConfig(fieldName);
    if (!config.value_as_field_option) return [];
    return getSortedOptionsByFieldName(
      fieldName as TemplateFieldName,
      this.args.programTemplate.tasFieldOptions
    ).map((option) => option.value);
  };

  get institutionErrors() {
    if (!this.args.hasAttempted) return [];
    const config = this.fieldConfig('SCHOOL_INSTITUTION_NAME');
    if (!config.required) return [];
    const value = getSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME' as FieldName,
      this.args.instanceFields
    );
    if (!value) return [this.intl.t('tas.guided.steps.field_required')];
    return [];
  }

  fieldErrors = (fieldName: string, programField: FormField) => {
    if (!this.args.hasAttempted) return [];
    const config = this.fieldConfig(fieldName);
    if (!config.required || programField.disabled) return [];
    const value = getSingleValueForTasField(fieldName as FieldName, this.args.instanceFields);
    if (!value) return [this.intl.t('tas.guided.steps.field_required')];
    return [];
  };

  <template>
    <Section>
      <:header>
        <div class="tio-row-tight">
          <span
            class="tio-inline-row-center w-7 h-7 rounded-full tio-bg-brand tio-fg-inverse tio-text-xs-bold"
          >{{this.displayNumber}}</span>
          <span>{{t "tas.guided.steps.enter_program_details"}}</span>
        </div>
      </:header>
      <:body>
        <p class="text-sm tio-fg-muted mb-6">{{t
            "tas.guided.steps.program_details_description"
          }}</p>

        {{#unless @isActiveParticipant}}
          <AlertBanner
            @intent="warning"
            @title={{t "tuition_assistance.inactive_participant_warnings.dashboard_title"}}
            class="mb-4"
          >
            <p class="text-sm">
              {{t "tuition_assistance.inactive_participant_warnings.dashboard_description"}}
            </p>
          </AlertBanner>
        {{/unless}}

        {{#each @programFormFields as |programField|}}
          {{#let (this.fieldConfig programField.name) as |config|}}
            {{#if (eq programField.name "SCHOOL_INSTITUTION_NAME")}}
              {{#if config.visible}}
                <div class="mb-6">
                  <SchoolSelect
                    @programTemplate={{@programTemplate}}
                    @labelClass="!tio-fg-secondary font-semibold block mb-1"
                    @selected={{@selectedSchool}}
                    @onChange={{@onSelectSchool}}
                    @errors={{this.institutionErrors}}
                    class="tio-col-extra-tight [&_label_span]:text-red-500"
                  />
                  {{#if @isLpnSchoolSelected}}
                    <AlertBanner
                      @intent="info"
                      @title={{t "lpn.application_banner.title"}}
                      class="mt-4"
                    >
                      <p class="text-sm">{{t "lpn.application_banner.body"}}</p>
                    </AlertBanner>
                  {{/if}}
                </div>
              {{/if}}
            {{else if config.visible}}
              <div class="mb-6">
                {{#if config.value_as_field_option}}
                  <ReFormField @label={{config.label}} @isRequired={{config.required}}>
                    <ReSelect
                      data-test={{programField.name}}
                      @items={{this.fieldOptions programField.name}}
                      @selected={{@getSingleValueForField programField.name}}
                      @onChange={{fn @onUpdateValueForField programField.name programField}}
                      @disabled={{programField.disabled}}
                      @clearable={{false}}
                      @placeholder={{t "tas.guided.select_an_option"}}
                      @errors={{this.fieldErrors programField.name programField}}
                    />
                  </ReFormField>
                {{else if (eq programField.mask "currency")}}
                  <Input
                    data-test={{programField.name}}
                    @label={{config.label}}
                    @isRequired={{config.required}}
                    @value={{divide
                      (or (Number (getSingleValueForTasField programField.name @instanceFields)) 0)
                      100
                    }}
                    @onChange={{fn @onUpdateValueForField programField.name programField}}
                    @isDisabled={{programField.disabled}}
                    {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2"}}
                  />
                {{else if (eq programField.type "date")}}
                  <ReFormField @label={{config.label}} @isRequired={{config.required}} class="mb-0">
                    <div class="relative">
                      <EmberFlatpickr
                        {{! @glint-expect-error: FieldValue type is broader than DateOption }}
                        @date={{or
                          (getSingleValueForTasField programField.name @instanceFields)
                          ""
                        }}
                        @onChange={{fn @onUpdateDateFieldValue programField.name}}
                        @dateFormat="Y-m-d"
                        @altInput={{true}}
                        @altFormat="F j, Y"
                        @disableMobile={{true}}
                        placeholder={{t "tas.guided.date_placeholder"}}
                        data-test={{programField.name}}
                        disabled={{programField.disabled}}
                        class="w-full rounded-lg border tio-border-default tio-bg-surface tio-fg-primary text-sm h-10 py-2 px-3 placeholder:tio-fg-muted hover:tio-border-strong focus:tio-border-brand focus:ring-2 focus:tio-ring-brand focus:outline-none transition-colors duration-150"
                      />
                      <div
                        class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none tio-fg-disabled"
                      >
                        <PhCalendarBlank @size="20" />
                      </div>
                    </div>
                  </ReFormField>
                {{else}}
                  <Input
                    data-test={{programField.name}}
                    @label={{config.label}}
                    @isRequired={{config.required}}
                    @value={{@getSingleValueForField programField.name}}
                    @onChange={{fn @onUpdateValueForField programField.name programField}}
                    @type={{this.asInputType programField.type}}
                    @isDisabled={{programField.disabled}}
                    @errors={{this.fieldErrors programField.name programField}}
                  />
                {{/if}}
              </div>
            {{/if}}
          {{/let}}
        {{/each}}

        <TuitionAssistanceFormsCustomFieldsForProvince
          @province="EMPLOYEE"
          @customFields={{@customEmployeeFields}}
          @didUpdateFields={{@onDidUpdateEmployeeCustomFields}}
          @formValidationErrors={{if @hasAttempted @customFieldValidationErrors (Array)}}
          @fieldContainerClass="mb-4"
          @programTemplate={{@programTemplate}}
        />
        <TuitionAssistanceFormsCustomFieldsForProvince
          @province="PROGRAM"
          @customFields={{@customProgramFields}}
          @didUpdateFields={{@onDidUpdateProgramCustomFields}}
          @formValidationErrors={{if @hasAttempted @customFieldValidationErrors (Array)}}
          @fieldContainerClass="mb-4"
          @programTemplate={{@programTemplate}}
        />

        <div class="flex pt-8">
          <Button @intent="primary" data-test-next-step {{on "click" @onNext}}>
            {{t "common.next"}}
          </Button>
        </div>
      </:body>
    </Section>
  </template>
}
